import { useEffect, useMemo, useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useListContext,
  useNotify,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { mockDriver } from "../../../../supabase/functions/_shared/messaging/driver.ts";
import { applyMergeFields } from "../../../../supabase/functions/_shared/templates.ts";
import { bulkSkipReason } from "../../../../supabase/functions/_shared/bulkSend.ts";
import { STUDIO_EMAIL } from "../../../../supabase/functions/_shared/vogelkop.ts";
import type { Contact, MessageTemplate } from "../types";

const leadAddress = (contact: Contact) => ({
  first_name: contact.first_name,
  last_name: contact.last_name,
  email: contact.email_jsonb?.find((item) => item.email)?.email ?? null,
  phone: contact.phone_jsonb?.find((item) => item.number)?.number ?? null,
  sms_consent: contact.sms_consent,
  sms_opt_out: contact.sms_opt_out,
  email_unsubscribed: contact.email_unsubscribed,
  do_not_contact: contact.do_not_contact,
});

export const BulkMessageButton = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const { filterValues } = useListContext();
  const { data: templates = [] } = useGetList<MessageTemplate>(
    "message_templates",
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const [open, setOpen] = useState(false);
  const channel = "email" as const;
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sending, setSending] = useState(false);
  const filterKey = JSON.stringify(filterValues ?? {});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    dataProvider
      .getList<Contact>("contacts", {
        filter: filterValues,
        pagination: { page: 1, perPage: 200 },
        sort: { field: "last_seen", order: "DESC" },
      })
      .then(({ data }) => {
        if (!cancelled) setContacts(data);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) notify("Could not load this list", { type: "error" });
      });
    return () => {
      cancelled = true;
    };
    // filterKey is the stable snapshot of filterValues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dataProvider, filterKey, notify]);

  const plan = useMemo(() => {
    const now = new Date();
    const recipients: Contact[] = [];
    const skipped: { contact: Contact; reason: string }[] = [];
    for (const contact of contacts) {
      const reason = bulkSkipReason(leadAddress(contact), channel, now);
      if (reason) skipped.push({ contact, reason });
      else recipients.push(contact);
    }
    return { recipients, skipped };
  }, [contacts, channel]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const template = templates.find((item) => String(item.id) === id);
    if (!template) return;
    if (template.channel !== "email") return;
    setBody(template.body);
    setSubject(template.subject ?? "");
  };

  const send = async () => {
    if (!body.trim() || plan.recipients.length === 0) return;
    setSending(true);
    const now = new Date();
    let sent = 0;
    try {
      for (const contact of plan.recipients) {
        const reason = bulkSkipReason(leadAddress(contact), channel, now);
        if (reason) continue;
        const values = {
          first_name: contact.first_name,
          last_name: contact.last_name,
          rep_name: identity?.fullName ?? "",
          property_address: contact.property_address,
        };
        const text = applyMergeFields(body, values);
        const mergedSubject = subject
          ? applyMergeFields(subject, values)
          : null;
        const to = leadAddress(contact).email;
        if (!to) continue;
        const from = STUDIO_EMAIL;
        const result = await mockDriver.send({
          channel,
          to,
          from,
          subject: mergedSubject,
          body: text,
        });
        await dataProvider.create("messages", {
          data: {
            contact_id: contact.id,
            sales_id: identity?.id,
            channel,
            direction: "outbound",
            status: result.status,
            subject: mergedSubject,
            body: text,
            from_address: from,
            to_address: to,
            provider: result.provider,
            provider_message_id: result.providerMessageId,
            sent_at: result.sentAt,
            delivered_at: result.deliveredAt ?? null,
            created_at: now.toISOString(),
          },
        });
        sent += 1;
      }
      notify(`Sent ${sent}. Skipped ${plan.skipped.length}.`);
      setOpen(false);
      setBody("");
      setSubject("");
      setTemplateId("");
    } catch (error) {
      console.error(error);
      notify("The bulk send stopped early", { type: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        Message list
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Message this list</DialogTitle>
            <DialogDescription>
              One email to the leads that match the current filters.
              Unsubscribes and do-not-contact are skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <select
                aria-label="Template"
                className="h-8 rounded-md border bg-transparent px-2 text-sm"
                value={templateId}
                onChange={(event) => applyTemplate(event.target.value)}
              >
                <option value="">Template</option>
                {templates
                  .filter((template) => template.channel === channel)
                  .map((template) => (
                    <option key={template.id} value={String(template.id)}>
                      {template.name}
                    </option>
                  ))}
              </select>
            </div>
            <Input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Subject"
              aria-label="Subject"
            />
            <Textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Message. {first_name} and {property_address} are filled for each lead."
              aria-label="Bulk message"
              rows={5}
            />
            <p className="text-sm">
              {plan.recipients.length} will receive it. {plan.skipped.length}{" "}
              skipped.
            </p>
            {plan.skipped.length > 0 ? (
              <ul className="max-h-28 overflow-auto text-sm text-muted-foreground">
                {plan.skipped.slice(0, 8).map(({ contact, reason }) => (
                  <li key={contact.id}>
                    {contact.first_name} {contact.last_name}: {reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={sending || !body.trim() || plan.recipients.length === 0}
              onClick={() => void send()}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
